import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  Input,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { UiControlValueAccessorDirective } from '../../basics/ui-control-value-accessor';
import { provideCustomCVA } from '../../basics/ui-custom-cva.provider';
import { merge, Observable, startWith, takeUntil } from 'rxjs';
import { getRandomId } from '../../utils/get-random-id';
import { BoschUiOptionBarItemComponent } from './bosch-ui-option-bar-item/bosch-ui-option-bar-item.component';
import { BoschUiOptionBarType } from './bosch-ui-option-bar.model';

@Component({
  selector: 'bosch-ui-option-bar',
  templateUrl: './bosch-ui-option-bar.component.html',
  styleUrls: ['./bosch-ui-option-bar.component.scss'],
  providers: [provideCustomCVA(BoschUiOptionBarComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BoschUiOptionBarComponent<ValueType>
  extends UiControlValueAccessorDirective<ValueType>
  implements AfterContentInit
{
  @Input() type: BoschUiOptionBarType = 'primary';
  @ContentChildren(BoschUiOptionBarItemComponent) items: QueryList<BoschUiOptionBarItemComponent<ValueType>> | null =
    null;

  private readonly OPTION_BAR_NAME: string = getRandomId('option-bar');

  ngAfterContentInit(): void {
    this.setOptionItemInitialParams();

    merge(...this.getOptionBarItemsStreams())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value: ValueType) => {
        this.onChange(value);
      });

    this.control.valueChanges
      .pipe(takeUntil(this.unsubscribe$), startWith(this.control.value))
      .subscribe((value: ValueType | null) => this.setOptionBarActiveState(value));
  }

  private setOptionItemInitialParams(): void {
    if (this.items?.length) {
      this.items.forEach((item: BoschUiOptionBarItemComponent<ValueType>) => {
        item.name = this.OPTION_BAR_NAME;
        item.disabled = this.control.disabled;
        item.type = this.type;
      });
    }
  }

  private getOptionBarItemsStreams(): Observable<ValueType>[] {
    if (this.items?.length) {
      return this.items.map((item: BoschUiOptionBarItemComponent<ValueType>) => item.value$);
    }

    return [];
  }

  private setOptionBarActiveState(value: ValueType | null): void {
    if (this.items?.length) {
      this.items.forEach((item: BoschUiOptionBarItemComponent<ValueType>) => (item.checked = item.value === value));
    }
  }
}
